import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import styled, { keyframes, css } from 'styled-components';

// Função para gerar um ID alfanumérico de comprimento específico
const generateAlphanumericId = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const glitch = keyframes`
  0% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  2% {
    clip-path: inset(10% 0 20% 0);
    transform: translate(-2px);
  }
  4% {
    clip-path: inset(40% 0 5% 0);
    transform: translate(2px);
  }
  6% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  50% { transform: translateY(100%); }
  100% { transform: translateY(-100%); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 rgba(64, 128, 255, 0); }
  50% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.3); }
  100% { box-shadow: 0 0 0 rgba(64, 128, 255, 0); }
`;

const buttonShimmer = keyframes`
  0% { background-position: -100px; }
  100% { background-position: 200px; }
`;

// Componentes estilizados
const FormContainer = styled.div`
  background: rgba(15, 25, 45, 0.7);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  position: relative;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(64, 128, 255, 0.5);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(64, 128, 255, 0.2);
  }
`;

const FormTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 1.1rem;
  
  .title-text {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  svg {
    color: #64B6FF;
  }
`;

const QuestionGroup = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(64, 128, 255, 0.2);
  position: relative;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.3s ease-out;
  
  &:hover {
    border-color: rgba(64, 128, 255, 0.4);
    background: rgba(0, 0, 0, 0.25);
  }
  
  ${props => props.active && css`
    border-color: rgba(64, 128, 255, 0.5);
    box-shadow: 0 0 10px rgba(64, 128, 255, 0.1);
  `}
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: -10px;
  left: 10px;
  background: linear-gradient(135deg, rgba(76, 110, 245, 0.7), rgba(59, 130, 246, 0.7));
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  z-index: 1;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(76, 110, 245, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: ${props => props.isLast ? '0' : '1rem'};
  transition: all 0.3s ease;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: rgba(100, 182, 255, 0.8);
  text-align: left;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(64, 128, 255, 0.5);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(20, 30, 60, 0.4);
  border: 1px solid rgba(64, 128, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.6rem;
  width: 100%;
  cursor: pointer;
  transition: all 0.25s ease;
  font-weight: 400;
  font-size: 0.85rem;
  margin-top: 1rem;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(2px);
  letter-spacing: 0.5px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100px;
    width: 100px;
    height: 100%;
    background: linear-gradient(
      90deg, 
      transparent, 
      rgba(64, 128, 255, 0.1), 
      transparent
    );
    animation: ${buttonShimmer} 3s infinite;
    animation-delay: 1s;
  }
  
  &:hover {
    background: rgba(30, 40, 80, 0.5);
    border-color: rgba(64, 128, 255, 0.3);
    color: rgba(255, 255, 255, 0.9);
    transform: translateY(-1px);
    animation: ${pulseGlow} 2s infinite;
  }
  
  &:active {
    transform: translateY(0);
    background: rgba(40, 60, 100, 0.6);
    border-color: rgba(64, 128, 255, 0.4);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    animation: none;
    &::before {
      animation: none;
    }
  }
  
  svg {
    opacity: 0.8;
    transition: all 0.25s ease;
  }
  
  &:hover svg {
    opacity: 1;
    transform: rotate(90deg);
  }
`;

const TechEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  border-radius: 12px;
  z-index: -1;
`;

const GridLine = styled.div`
  position: absolute;
  background: rgba(64, 128, 255, 0.05);
  
  ${props => props.horizontal ? `
    height: 1px;
    width: 100%;
    left: 0;
    top: ${props.position}%;
  ` : `
    width: 1px;
    height: 100%;
    top: 0;
    left: ${props.position}%;
  `}
`;

const DataPoint = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(64, 128, 255, ${props => props.opacity || 0.3});
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  animation: ${pulse} ${props => props.duration || 2}s infinite;
  animation-delay: ${props => props.delay || 0}s;
`;

const Scanline = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: rgba(64, 128, 255, 0.3);
  opacity: 0.5;
  z-index: 1;
  animation: ${scanline} 4s linear infinite;
  pointer-events: none;
`;

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const QuestionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#64B6FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MAX_QUESTIONS = 5;

const QualifyingQuestionForm = forwardRef(({ onAddQuestion, existingQuestions }, ref) => {
  const [questions, setQuestions] = useState([
    { id: generateAlphanumericId(16), question: '', disqualifyingAnswer: '' }
  ]);
  
  const inputRefs = useRef([]);
  
  // Expor o método getValidQuestions através da referência
  useImperativeHandle(ref, () => ({
    getValidQuestions: () => {
      return questions.filter(q => q.question.trim() !== '');
    }
  }));
  
  // Gerar linhas de grade
  const gridLines = [];
  for (let i = 0; i < 5; i++) {
    gridLines.push(<GridLine key={`h-${i}`} horizontal position={i * 25} />);
    gridLines.push(<GridLine key={`v-${i}`} position={i * 25} />);
  }

  // Gerar pontos de dados
  const dataPoints = [];
  for (let i = 0; i < 15; i++) {
    dataPoints.push(
      <DataPoint 
        key={i}
        top={Math.random() * 100}
        left={Math.random() * 100}
        opacity={Math.random() * 0.5 + 0.1}
        duration={(Math.random() * 3) + 1}
        delay={Math.random() * 2}
      />
    );
  }

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };
  
  const addNewQuestion = () => {
    if (questions.length < MAX_QUESTIONS) {
      const newQuestions = [...questions, { id: generateAlphanumericId(16), question: '', disqualifyingAnswer: '' }];
      setQuestions(newQuestions);
      
      // Focar no novo campo após renderização
      setTimeout(() => {
        if (inputRefs.current[questions.length]) {
          inputRefs.current[questions.length].focus();
        }
      }, 100);
    }
  };
  
  // Verificar se a pergunta tem conteúdo para mostrar o campo de resposta desqualificatória
  const shouldShowDisqualifyingAnswer = (index) => {
    return questions[index].question.trim() !== '';
  };
  
  // Atualizar as perguntas no componente pai apenas quando necessário
  useEffect(() => {
    // Não fazer nada aqui, vamos atualizar o componente pai apenas quando o usuário
    // clicar no botão de salvar o produto
  }, [questions]);
  
  return (
    <FormContainer>
      <TechEffect>
        {gridLines}
        {dataPoints}
      </TechEffect>
      <Scanline />
      
      {questions.map((question, index) => (
        <QuestionGroup key={question.id} active={index === questions.length - 1}>
          <QuestionNumber>{index + 1}</QuestionNumber>
          <InputGroup>
            <InputLabel>
              Pergunta
            </InputLabel>
            <StyledInput
              ref={el => inputRefs.current[index] = el}
              type="text"
              placeholder="Digite uma pergunta qualificatória ou deixe em branco se não tiver"
              value={question.question}
              onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
            />
          </InputGroup>
          
          {/* Mostrar o campo de resposta desqualificatória apenas quando a pergunta tiver conteúdo */}
          {shouldShowDisqualifyingAnswer(index) && (
            <InputGroup isLast>
              <InputLabel>
                Resposta Desqualificatória (opcional)
              </InputLabel>
              <StyledInput
                type="text"
                placeholder="Digite uma resposta que desqualifica o lead (opcional)"
                value={question.disqualifyingAnswer}
                onChange={(e) => handleQuestionChange(index, 'disqualifyingAnswer', e.target.value)}
              />
            </InputGroup>
          )}
        </QuestionGroup>
      ))}
      
      {questions.length < MAX_QUESTIONS && (
        <AddButton 
          onClick={addNewQuestion}
          disabled={questions[questions.length - 1].question.trim() === ''}
        >
          <PlusIcon /> Nova pergunta
        </AddButton>
      )}
    </FormContainer>
  );
});

export default QualifyingQuestionForm;
